import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalImageThumbnail from 'LuxKit/Modal/ModalImageThumbnail'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import type { ImageParams } from 'components/Common/ResponsiveImage/ResponsiveImage'
import List from 'components/Common/List'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'
import LoyaltyTierMessageBanner from 'loyalty/components/LoyaltyTierMessageBanner'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'
import { useAppSelector } from 'hooks/reduxHooks'
import { FormattedMessage, useIntl } from 'react-intl'
import TextLink from 'LuxKit/TextLink'
import React from 'react'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { useLoyaltyEsimImage } from 'loyalty/constants/loyaltyEsimConstants'
import { rem } from 'polished'
import styled from 'styled-components'

/**
 * FormattedMessage rich text uses a Fragment; a vertical Group would treat each fragment child as a
 * separate flex item and stack the two parts. This wrapper keeps “Available to redeem” and the GB
 * line on one row (wrap only if the viewport is too narrow).
 */
const EsimAvailabilityRedeemLine = styled.span`
  display: inline-flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${rem(4)};
`

const imageParams: ImageParams = {
  mobileWidth: '560px',
  width: '100%',
  fit: 'center',
  objectFit: 'contain',
}

interface LoyaltyEsimBenefitsInfoContentProps {
  title?: React.ReactNode
}

function LoyaltyEsimBenefitsInfoContent({ title }: LoyaltyEsimBenefitsInfoContentProps) {
  const intl = useIntl()
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const tierDisplayName = tier ? getLoyaltyTierDisplayName(tier) : ''
  const benefit = useLoyaltyUserBenefitView('esim_package')
  const gbValue = benefit?.benefit?.value?.amount
  const esimImage = useLoyaltyEsimImage()

  if (!gbValue) return null

  /** Tier allowance from `esim_package` benefit (same source as the paragraph below). */
  const redeemAvailabilityLabel = intl.formatMessage(
    {
      id: 'rGsV4LgxHRV5chlm1swL',
      defaultMessage: '{amount}GB per trip',
      description: 'Label showing eSIM data allowance per trip for account benefit tile',
    },
    { amount: gbValue },
  )

  const unlockEndContent = (
    <BodyText variant="small">
      {intl.formatMessage({
        id: 'Ay2TQdEyW64P1EJeAyMz',
        defaultMessage: 'Unlocks 7 days before departure',
        description: 'Text indicating when the eSIM promo code becomes available',
      })}
    </BodyText>
  )

  return <>
    {title && <ModalContent>{title}</ModalContent>}
    <ModalImageThumbnail image={esimImage} imageParams={imageParams} />
    <ModalContent>
      <Group direction="vertical" gap={24}>
        {tier && (
          <LoyaltyTierMessageBanner
            tier={tier}
            density="compact"
            icon={<LoyaltyTierIcon tier={tier} size={24} />}
            endContent={
              <CSSBreakpoint min="tablet">
                {unlockEndContent}
              </CSSBreakpoint>
            }
          >
            <Group direction="vertical" gap={4}>
              <EsimAvailabilityRedeemLine>
                <FormattedMessage
                  id="WLAf5zLL6abH8loOG_Sk"
                  defaultMessage="Available to redeem <availability>{detail}</availability>"
                  description="Availability status message in the loyalty eSIM banner (bold: GB per trip or unlimited)"
                  values={{
                    detail: redeemAvailabilityLabel,
                    availability: (chunks: React.ReactNode) => (
                      <BodyText variant="medium" weight="bold" as="span">
                        {chunks}
                      </BodyText>
                    ),
                  }}
                />
              </EsimAvailabilityRedeemLine>
              <CSSBreakpoint max="mobile">
                {unlockEndContent}
              </CSSBreakpoint>
            </Group>
          </LoyaltyTierMessageBanner>
        )}

        <BodyText variant="medium">
          {intl.formatMessage({
            id: 'upftm5-WWLnQ-RRkLE7J',
            defaultMessage: 'As a Société {tierName} member, you\'ll receive {gbValue}GB Free Data Roaming on every eligible international booking — so you\'re connected the moment you arrive.',
            description: 'Intro description of the eSIM benefit in the details modal',
          }, { gbValue, tierName: tierDisplayName })}
        </BodyText>

        <VerticalSpacer gap={4}>
          <Heading variant="heading5">
            {intl.formatMessage({
              id: 'LBwlvbQkyeCotDSLCAY-',
              defaultMessage: 'How it works',
              description: 'Section heading explaining how the eSIM benefit works for members',
            })}
          </Heading>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'N2pDGgVkQmGq_p7O8KN8',
              defaultMessage: 'Activate your eSIM before you fly and enjoy data as soon as you land.',
              description: 'Sub-description explaining how the eSIM benefit activates before travel',
            })}
          </BodyText>
          <BodyText variant="medium" as="div">
            <List>
              <li>
                {intl.formatMessage({
                  id: 'XKeTzPiQFPay1OmjJuC1',
                  defaultMessage: 'No roaming fees',
                  description: 'eSIM benefit bullet point highlighting no international roaming fees',
                })}
              </li>
              <li>
                {intl.formatMessage({
                  id: 'gimEMCp1BShQzaxR2bMr',
                  defaultMessage: 'No physical SIM required',
                  description: 'eSIM benefit bullet point noting no physical SIM card is required',
                })}
              </li>
              <li>
                {intl.formatMessage({
                  id: 'FOZLX6fH1s8AMyvrE6qZ',
                  defaultMessage: 'Keep your number',
                  description: 'eSIM benefit bullet point noting you can keep your existing phone number',
                })}
              </li>
              <li>
                {intl.formatMessage({
                  id: 'jUhhTTUxdwvoDW2Bb7LC',
                  defaultMessage: 'Works instantly',
                  description: 'eSIM benefit bullet point highlighting the eSIM activates instantly',
                })}
              </li>
            </List>
          </BodyText>
        </VerticalSpacer>

        <VerticalSpacer gap={8}>
          <Heading variant="heading5">
            {intl.formatMessage({
              id: 'M0GeQdqxX3O-QcKm_cQ3',
              defaultMessage: 'What is an eSIM?',
              description: 'Section heading explaining what an eSIM is for unfamiliar users',
            })}
          </Heading>
          <BodyText variant="medium">
            <FormattedMessage
              id="Pf9qD3xtCGyFdqq0c-JX"
              defaultMessage="An eSIM is a digital SIM card already built into your phone. It gives you instant mobile internet abroad, no physical SIM needed. <link>Check if your phone supports eSIM</link>"
              description="Detailed description explaining what an eSIM is with link to Hubby compatibility page"
              values={{
                link: (chunks: React.ReactNode) => <TextLink href="https://www.hubbyesim.com/compatible-phones" target="_blank" rel="noopener noreferrer">{chunks}</TextLink>,
              }}
            />
          </BodyText>
        </VerticalSpacer>

      </Group>
    </ModalContent>
  </>
}

export default LoyaltyEsimBenefitsInfoContent
